import { t as tr } from '../i18n.js'
import { useState } from 'react'
import { Check, X } from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import { api, type PayrollPerson } from '../api.js'
import { fmtMinutes, mondayOf, addDays } from '../time.js'

/** Period close: readiness per person and the CSV export (managers/admins). */
export function PayrollPage() {
  const thisMonday = mondayOf(new Date().toISOString().slice(0, 10))
  const [from, setFrom] = useState(addDays(thisMonday, -28))
  const [to, setTo] = useState(addDays(thisMonday, -7))
  const q = useQuery({ queryKey: ['payroll', from, to], queryFn: () => api<{ people: PayrollPerson[] }>(`/payroll/readiness?from=${from}&to=${to}`), enabled: !!from && !!to })
  const people = q.data?.people ?? []
  const blocked = people.filter((p) => !p.ready)
  const href = (partial: boolean) => `/api/payroll/export.csv?from=${from}&to=${to}${partial ? '&allowPartial=true' : ''}`
  return (
    <>
      <h1>{tr("Payroll export")}</h1>
      <ul className="help muted"><li>{tr("A person is")} <strong>{tr("payroll-ready")}</strong> only when every project-week they touched in the range is approved.</li><li>{tr("Pick the range by")} <strong>{tr("week start")}</strong> (Mondays, in each project's own timezone).</li></ul>
      <div className="row toolbar">
        <label className="inline small">{tr("Weeks from")} <input type="date" value={from} onChange={(e) => setFrom(mondayOf(e.target.value))} /></label>
        <label className="inline small">{tr("to")} <input type="date" value={to} onChange={(e) => setTo(mondayOf(e.target.value))} /></label>
        <span className="spacer" />
        {blocked.length === 0 && people.length > 0
          ? <a className="button primary" href={href(false)} data-tip={tr("Everyone in the range is approved — download the payroll CSV")}>{tr("Download CSV")}</a>
          : <a className="button" href={href(true)} data-tip={tr("Excludes people who still have unapproved weeks; they are named in the file header")}>{tr("Download partial CSV")}</a>}
      </div>
      {blocked.length > 0 && <p className="notice bad-border small"><strong>{blocked.length} {blocked.length === 1 ? tr("person is") : tr("people are")} not ready:</strong> {blocked.map((b) => `${b.name} (${b.outstanding.map((o) => `${o.projectCode} ${o.weekStart} ${o.state === 'open' ? 'not submitted' : o.state}`).join(', ')})`).join('; ')}. Chase the submissions, or export partially.</p>}
      <section className="card">
        <table className="table">
          <thead><tr><th>{tr("Person")}</th><th className="num">{tr("Hours")}</th><th className="num">{tr("Approved")}</th><th className="num">{tr("Leave")}</th><th>{tr("Ready")}</th><th>{tr("Outstanding")}</th></tr></thead>
          <tbody>
            {people.map((p) => (
              <tr key={p.userId} className={p.ready ? '' : 'muted'}>
                <td>{p.name} <span className="small muted">{p.email}</span></td>
                <td className="num mono">{fmtMinutes(p.totalMinutes)}</td>
                <td className="num mono">{fmtMinutes(p.approvedMinutes)}</td>
                <td className="num mono">{p.leaveHours ? `${p.leaveHours}h` : ''}</td>
                <td>{p.ready ? <span className="good" data-tip={tr("Every project-week approved")}><Check size={14} strokeWidth={2} aria-hidden /></span> : <span className="bad" data-tip={tr("Has unapproved weeks")}><X size={14} strokeWidth={2} aria-hidden /></span>}</td>
                <td className="small">{p.outstanding.map((o) => `${o.projectCode} ${o.weekStart}`).join(', ')}</td>
              </tr>
            ))}
            {people.length === 0 && q.data && <tr><td colSpan={6} className="muted small">{tr("No time in this range.")}</td></tr>}
          </tbody>
        </table>
      </section>
    </>
  )
}
